const TEXT_ENTRY_TAGS = new Set(["TEXTAREA", "SELECT"]);
const TEXT_ENTRY_INPUT_TYPES = new Set(["text", "search", "tel", "url", "email", "password", "number"]);
const EDITABLE_ROLES = new Set(["combobox", "listbox", "option", "textbox"]);
const AUTOCOMPLETE_POPPER_SELECTOR = ".MuiAutocomplete-popper";
const GRID_EDITING_SELECTOR = ".ag-cell-inline-editing, .ag-cell-popup-editing";

const isTextEntryInput = (node: HTMLElement): boolean => {
	if (node.tagName !== "INPUT") return false;
	const input = node as HTMLInputElement;
	if (input.readOnly || input.disabled) return false;
	return TEXT_ENTRY_INPUT_TYPES.has(input.type);
};

export const isEditableElement = (node: EventTarget | null): boolean => {
	if (!(node instanceof HTMLElement)) return false;
	if (TEXT_ENTRY_TAGS.has(node.tagName)) return true;
	if (isTextEntryInput(node)) return true;
	if (node.isContentEditable) return true;
	const contentEditable = node.getAttribute("contenteditable");
	if (contentEditable !== null && contentEditable !== "false") return true;
	const role = node.getAttribute("role");
	return role !== null && EDITABLE_ROLES.has(role);
};

export const isAutocompletePopperOpen = (doc: Document = document): boolean =>
	doc.querySelector(AUTOCOMPLETE_POPPER_SELECTOR) !== null;

const shouldSuppressForInputs = (
	event: Pick<KeyboardEvent, "isComposing" | "target">,
	doc: Document = document
): boolean => {
	if (event.isComposing) return true;
	if (isEditableElement(event.target)) return true;
	if (isEditableElement(doc.activeElement)) return true;
	if (isAutocompletePopperOpen(doc)) return true;
	return doc.querySelector(GRID_EDITING_SELECTOR) !== null;
};

export type ScanSelectionSource = "camera" | "tap" | "wedge";

const SCAN_SOURCES_THAT_MUST_LEAVE_FOCUS_FREE: readonly ScanSelectionSource[] = ["wedge"];

export const POST_SCAN_FOCUS_DELAY_MS = 200;

export const shouldFocusInputAfterScan = (source: ScanSelectionSource): boolean =>
	!SCAN_SOURCES_THAT_MUST_LEAVE_FOCUS_FREE.includes(source);

let pendingFocusTimer: ReturnType<typeof setTimeout> | undefined;

export const cancelPendingFocusAfterScan = (): void => {
	if (pendingFocusTimer === undefined) return;
	clearTimeout(pendingFocusTimer);
	pendingFocusTimer = undefined;
};

// A closed window still reports a non-null defaultView, so only typeof survives teardown.
const domGlobalsAreTornDown = (): boolean => typeof document === "undefined" || typeof HTMLElement === "undefined";

export const focusInputAfterScan = (
	source: ScanSelectionSource,
	target: () => HTMLElement | null | undefined
): void => {
	if (!shouldFocusInputAfterScan(source)) return;
	cancelPendingFocusAfterScan();
	const doc = document;
	const focusedAtScan = doc.activeElement;
	pendingFocusTimer = setTimeout(() => {
		pendingFocusTimer = undefined;
		if (domGlobalsAreTornDown()) return;
		const focusedNow = doc.activeElement;
		if (focusedNow !== focusedAtScan && isEditableElement(focusedNow)) return;
		target()?.focus();
	}, POST_SCAN_FOCUS_DELAY_MS);
};

export default shouldSuppressForInputs;
